import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { turkishIncludes } from '../../../lib/turkishSearch';
import { calculateTotals } from '../utils/calculations';
import { getDefaultFormValues } from '../utils/constants';
import { useTranslation } from '../../../lib/i18n';

export function useRoutes(user) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [routes, setRoutes] = useState([]);
  const [firms, setFirms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state — URL-backed so back-navigation restores filters
  const [selectedCustomers, setSelectedCustomers] = useState(() => {
    const raw = searchParams.get('customers');
    return raw ? new Set(raw.split(',').filter(Boolean)) : new Set(['fedex']);
  });
  const [selectedFirm, setSelectedFirm] = useState(() => searchParams.get('firm') || '');
  const [selectedMonths, setSelectedMonths] = useState(() => {
    const raw = searchParams.get('months');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [dateRange, setDateRange] = useState(() => ({
    start: searchParams.get('from') || '',
    end: searchParams.get('to') || '',
  }));
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');

  // Sync filters → URL
  useEffect(() => {
    const next = new URLSearchParams();
    const customersArr = Array.from(selectedCustomers);
    const isDefaultCustomers = customersArr.length === 1 && customersArr[0] === 'fedex';
    if (customersArr.length > 0 && !isDefaultCustomers) next.set('customers', customersArr.join(','));
    if (selectedFirm) next.set('firm', selectedFirm);
    if (selectedMonths.length > 0) next.set('months', selectedMonths.join(','));
    if (dateRange.start) next.set('from', dateRange.start);
    if (dateRange.end) next.set('to', dateRange.end);
    if (searchQuery) next.set('q', searchQuery);
    setSearchParams(next, { replace: true });
  }, [selectedCustomers, selectedFirm, selectedMonths, dateRange, searchQuery, setSearchParams]);
  
  // Bulk selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState(getDefaultFormValues());
  const [editingRoute, setEditingRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [addDialog, setAddDialog] = useState(false);
  const [lastModalCustomer, setLastModalCustomer] = useState(null);
  
  // Helpers
  const isMultiCustomer = selectedCustomers.size > 1;
  const primaryCustomer = Array.from(selectedCustomers)[0] || 'fedex';

  // Fetch routes
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      
      const customersArray = Array.from(selectedCustomers);
      if (customersArray.length === 0) {
        setRoutes([]);
        setLoading(false);
        return;
      }
      
      const allRoutes = [];
      for (const customer of customersArray) {
        const params = new URLSearchParams();
        params.append('customer', customer);
        if (selectedFirm) params.append('firm_id', selectedFirm);
        // Date range takes priority over months
        if (dateRange.start && dateRange.end) {
          params.append('start_date', dateRange.start);
          params.append('end_date', dateRange.end);
        } else if (selectedMonths.length > 0) {
          params.append('month', selectedMonths.join(','));
        }
        
        const response = await api.get(`/routes?${params.toString()}`);
        if (response.data.success) {
          allRoutes.push(...(response.data.data || []));
        }
      }
      
      allRoutes.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRoutes(allRoutes);
    } catch (error) {
      console.error('Error fetching routes:', error);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomers, selectedFirm, selectedMonths, dateRange]);

  // Fetch firms
  const fetchFirms = useCallback(async () => {
    try {
      const response = await api.get('/firms/');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching firms:', error);
    }
  }, []);

  // Fetch employees
  const fetchEmployees = useCallback(async (firmId) => {
    try {
      const params = new URLSearchParams();
      if (firmId) params.append('firm_id', firmId);
      
      const response = await api.get(`/employee-profile/list?${params.toString()}`);
      if (response.data.success) {
        const allEmployees = response.data.data?.employees || [];
        const filteredEmployees = allEmployees.filter(emp => emp.user?.role !== 'SUPER_ADMIN');
        setEmployees(filteredEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  }, []);

  // Fetch vehicles
  const fetchVehicles = useCallback(async (firmId) => {
    try {
      const params = new URLSearchParams();
      if (firmId) params.append('firm_id', firmId);
      
      const response = await api.get(`/vehicles?${params.toString()}`);
      if (response.data.success) {
        setVehicles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  }, []);

  // Fetch customers from DB
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers');
      if (response.data.success) {
        setCustomerList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchRoutes();
    fetchFirms();
    fetchCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch routes when filters change
  useEffect(() => {
    fetchRoutes();
  }, [selectedCustomers, selectedFirm, selectedMonths, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch employees when form firm_id changes
  useEffect(() => {
    if (formData.firm_id) {
      fetchEmployees(formData.firm_id);
      fetchVehicles(formData.firm_id);
    }
  }, [formData.firm_id, fetchEmployees, fetchVehicles]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [selectedCustomers, selectedFirm, selectedMonths, dateRange]);

  // Toggle customer selection
  const toggleCustomer = useCallback((customerId) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        if (newSet.size > 1) {
          newSet.delete(customerId);
        }
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  }, []);

  // Save route
  const handleSaveRoute = useCallback(async (attachmentsRef) => {
    try {
      setSaving(true);
      
      const customerConfig = customerList.find(c => c.slug === formData.customer);
      const calcType = customerConfig?.calculation_type || null;
      const calculated = calculateTotals(formData, calcType);
      const routeData = {
        customer: formData.customer || primaryCustomer,
        firm_id: formData.firm_id,
        date: formData.date,
        route_number: formData.route_number || null,
        route_type: formData.route_type || null,
        rit_number: formData.rit_number || null,
        plaats: formData.plaats || null,
        license_plate: formData.license_plate,
        driver_id: formData.driver_id || null,
        driver_name: formData.driver_name,
        copilot_id: formData.copilot_id || null,
        copilot_name: formData.copilot_name || null,
        km_start: formData.km_start ? parseInt(formData.km_start) : null,
        km_end: formData.km_end ? parseInt(formData.km_end) : null,
        km_total: calculated.km_total || null,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        start_cost: parseFloat(formData.start_cost) || 0.0,
        stops: formData.stops ? parseInt(formData.stops) : null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        break_time: formData.break_time,
        work_hours: calculated.work_hours,
        work_cost: calculated.work_cost,
        total_cost: calculated.total_cost,
        notes: formData.notes || null
      };
      
      let response;
      let newRouteId = null;
      
      if (editingRoute) {
        response = await api.put(`/routes/item/${editingRoute.id}`, routeData);
      } else {
        response = await api.post('/routes', routeData);
        // Get the new route ID for uploading pending attachments
        if (response.data.success && response.data.data?.id) {
          newRouteId = response.data.data.id;
        }
      }
      
      if (response.data.success) {
        // Upload pending attachments for new routes
        if (!editingRoute && newRouteId && attachmentsRef?.current) {
          const hasPending = attachmentsRef.current.hasPendingFiles?.();
          if (hasPending) {
            try {
              await attachmentsRef.current.uploadPendingFiles(newRouteId);
            } catch (uploadError) {
              console.error('Error uploading pending attachments:', uploadError);
              toast.error('Bazı dosyalar yüklenemedi');
            }
          }
        }
        
        toast.success(editingRoute ? t('routes.messages.routeUpdated') : t('routes.messages.routeAdded'));
        setAddDialog(false);
        setEditingRoute(null);
        resetForm(formData.customer || 'fedex');
        fetchRoutes();
      }
    } catch (error) {
      console.error('Error saving route:', error);
      toast.error(t('routes.messages.saveError'));
    } finally {
      setSaving(false);
    }
  }, [formData, editingRoute, primaryCustomer, fetchRoutes, t, selectedFirm, customerList]);

  // Delete route
  const handleDeleteRoute = useCallback(async (routeId) => {
    if (!confirm(t('routes.confirmDelete'))) return;
    
    try {
      const response = await api.delete(`/routes/item/${routeId}`);
      if (response.data.success) {
        toast.success(t('routes.messages.routeDeleted'));
        fetchRoutes();
      }
    } catch (error) {
      console.error('Error deleting route:', error);
      const msg = error.response?.data?.detail || t('routes.messages.deleteError');
      toast.error(msg);
    }
  }, [fetchRoutes, t]);

  // Clone route — Şub 27, 2026: Artık direkt POST etmiyor;
  // FuelReports pattern'i gibi form modalı'nı önceden dolu şekilde açıyor.
  // Kullanıcı "Kaydet" derse kayıt oluşuyor.
  const handleCloneRoute = useCallback(async (route) => {
    try {
      // Detay için orijinal kaydı çek (bazı alanlar list response'da eksik olabilir)
      let sourceRoute = route;
      try {
        const routeResponse = await api.get(`/routes/item/${route.id}`);
        if (routeResponse.data.success) sourceRoute = routeResponse.data.data;
      } catch (_) { /* fallback: list satırı kullan */ }

      const today = new Date().toISOString().split('T')[0];
      setFormData({
        customer: sourceRoute.customer,
        firm_id: sourceRoute.firm_id || '',
        date: today,
        route_number: sourceRoute.route_number || '',
        route_type: sourceRoute.route_type || '',
        rit_number: sourceRoute.rit_number || '',
        plaats: sourceRoute.plaats || '',
        license_plate: sourceRoute.license_plate || '',
        driver_id: sourceRoute.driver_id || '',
        driver_name: sourceRoute.driver_name || '',
        copilot_id: sourceRoute.copilot_id || '',
        copilot_name: sourceRoute.copilot_name || '',
        km_start: sourceRoute.km_start?.toString() || '',
        km_end: sourceRoute.km_end?.toString() || '',
        hourly_rate: sourceRoute.hourly_rate?.toString() || '0',
        start_cost: sourceRoute.start_cost?.toString() || '0',
        stops: sourceRoute.stops?.toString() || '',
        start_time: sourceRoute.start_time || '08:00',
        end_time: sourceRoute.end_time || '17:00',
        break_time: sourceRoute.break_time || (sourceRoute.customer === 'mover' ? '00:30' : '00:00'),
        notes: sourceRoute.notes || ''
      });
      setEditingRoute(null); // yeni kayıt akışı
      setAddDialog(true);
    } catch (error) {
      console.error('Error preparing clone:', error);
      toast.error(t('routes.messages.errorOccurred'));
    }
  }, [setFormData, setEditingRoute, setAddDialog, t]);

  // Bulk clone — Şub 27, 2026: Seçili tüm kayıtları bugünün tarihiyle
  // doğrudan (modal göstermeden) klonlar. Yeni oluşan id'ler
  // `recentlyClonedIds`'e eklenir → tablo satırları vurgulanır.
  const [recentlyClonedIds, setRecentlyClonedIds] = useState(new Set());
  const [bulkCloning, setBulkCloning] = useState(false);

  const handleBulkClone = useCallback(async () => {
    if (selectedRows.size === 0) return;
    try {
      setBulkCloning(true);
      const today = new Date().toISOString().split('T')[0];
      const ids = Array.from(selectedRows);
      const results = await Promise.allSettled(
        ids.map(async (routeId) => {
          const detail = await api.get(`/routes/item/${routeId}`);
          const src = detail.data.success ? detail.data.data : null;
          if (!src) throw new Error('source missing');
          const clonedData = {
            customer: src.customer,
            firm_id: src.firm_id,
            date: today,
            route_number: src.route_number || '',
            route_type: src.route_type || '',
            rit_number: src.rit_number || '',
            plaats: src.plaats || '',
            license_plate: src.license_plate || '',
            driver_id: src.driver_id || '',
            driver_name: src.driver_name || '',
            copilot_id: src.copilot_id || '',
            copilot_name: src.copilot_name || '',
            km_start: src.km_start || 0,
            km_end: src.km_end || 0,
            hourly_rate: src.hourly_rate || 0,
            start_cost: src.start_cost || 0,
            stops: src.stops || 0,
            start_time: src.start_time || '08:00',
            end_time: src.end_time || '17:00',
            break_time: src.break_time || (src.customer === 'mover' ? '00:30' : '00:00'),
            notes: src.notes || '',
          };
          const resp = await api.post('/routes', clonedData);
          if (!resp.data.success) throw new Error(resp.data.message || 'failed');
          return resp.data.data?.id || resp.data.data?._id || null;
        })
      );
      const okIds = results
        .filter((r) => r.status === 'fulfilled' && r.value)
        .map((r) => r.value);
      const failCount = results.filter((r) => r.status === 'rejected').length;
      if (okIds.length > 0) {
        toast.success(
          `${okIds.length} ${t('routes.messages.routeCopied') || 'kayıt kopyalandı'}${failCount ? `, ${failCount} ${t('routes.messages.copyFailed') || 'başarısız'}` : ''}`
        );
        setRecentlyClonedIds((prev) => {
          const next = new Set(prev);
          okIds.forEach((id) => next.add(id));
          return next;
        });
        // 30 sn sonra vurguyu temizle
        setTimeout(() => {
          setRecentlyClonedIds((prev) => {
            const next = new Set(prev);
            okIds.forEach((id) => next.delete(id));
            return next;
          });
        }, 30000);
        setSelectedRows(new Set());
        await fetchRoutes();
      } else {
        toast.error(t('routes.messages.copyFailed') || 'Kopyalama başarısız');
      }
    } catch (error) {
      console.error('Bulk clone error:', error);
      toast.error(t('routes.messages.errorOccurred'));
    } finally {
      setBulkCloning(false);
    }
  }, [selectedRows, fetchRoutes, t]);

  // Bulk selection
  const toggleRowSelection = useCallback((routeId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
  }, []);

  const toggleAllRows = useCallback((filteredRoutes) => {
    if (selectedRows.size === filteredRoutes.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredRoutes.map(r => r.id)));
    }
  }, [selectedRows.size]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) {
      toast.error(t('routes.messages.selectRecordsToDelete'));
      return;
    }
    
    if (!confirm(t('routes.messages.bulkDeleteConfirm').replace('{count}', selectedRows.size))) return;
    
    try {
      setBulkDeleting(true);
      const deletePromises = Array.from(selectedRows).map(routeId => 
        api.delete(`/routes/item/${routeId}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedRows.size} ${t('routes.messages.bulkDeleteSuccess')}`);
      setSelectedRows(new Set());
      fetchRoutes();
    } catch (error) {
      console.error('Bulk delete error:', error);
      const msg = error.response?.data?.detail || t('routes.messages.bulkDeleteError2');
      toast.error(msg);
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedRows, fetchRoutes, t]);

  // Form helpers
  const resetForm = useCallback((customerType = 'fedex') => {
    const customerConfig = customerList.find(c => c.slug === customerType);
    const defaults = customerConfig?.default_values || {};
    
    setFormData({
      customer: customerType,
      firm_id: selectedFirm,
      date: new Date().toISOString().split('T')[0],
      route_number: '',
      route_type: '',
      rit_number: '',
      plaats: '',
      license_plate: '',
      driver_id: '',
      driver_name: '',
      copilot_id: '',
      copilot_name: '',
      km_start: '',
      km_end: '',
      hourly_rate: defaults.hourly_rate || '0',
      start_cost: defaults.start_cost || '0',
      stops: '',
      start_time: defaults.start_time || '08:00',
      end_time: defaults.end_time || '17:00',
      break_time: defaults.break_time || '00:00',
      notes: ''
    });
  }, [selectedFirm, customerList]);

  const handleFormCustomerChange = useCallback((newCustomer) => {
    const customerConfig = customerList.find(c => c.slug === newCustomer);
    const defaults = customerConfig?.default_values || {};
    const fields = customerConfig?.fields || [];
    
    setLastModalCustomer(newCustomer);
    setFormData(prev => ({
      ...prev,
      customer: newCustomer,
      hourly_rate: defaults.hourly_rate || '0',
      start_cost: defaults.start_cost || '0',
      break_time: defaults.break_time || '00:00',
      start_time: defaults.start_time || prev.start_time || '08:00',
      end_time: defaults.end_time || prev.end_time || '17:00',
      route_number: fields.includes('route_number') ? prev.route_number : '',
      route_type: fields.includes('route_type') ? prev.route_type : '',
      rit_number: '',
      plaats: fields.includes('rit') ? prev.plaats : '',
      copilot_id: fields.includes('copilot') ? prev.copilot_id : '',
      copilot_name: fields.includes('copilot') ? prev.copilot_name : '',
      km_start: fields.includes('km_start') ? prev.km_start : '',
      km_end: fields.includes('km_end') ? prev.km_end : '',
      stops: fields.includes('stops') ? prev.stops : '',
    }));
  }, [customerList]);

  const openAddDialog = useCallback(() => {
    resetForm(lastModalCustomer || primaryCustomer || 'fedex');
    setEditingRoute(null);
    setAddDialog(true);
  }, [resetForm, lastModalCustomer, primaryCustomer]);

  const openEditDialog = useCallback((route) => {
    setFormData({
      customer: route.customer,
      firm_id: route.firm_id || '',
      date: route.date?.split('T')[0] || '',
      route_number: route.route_number || '',
      route_type: route.route_type || '',
      rit_number: route.rit_number || '',
      plaats: route.plaats || '',
      license_plate: route.license_plate || '',
      driver_id: route.driver_id || '',
      driver_name: route.driver_name || '',
      copilot_id: route.copilot_id || '',
      copilot_name: route.copilot_name || '',
      km_start: route.km_start?.toString() || '',
      km_end: route.km_end?.toString() || '',
      hourly_rate: route.hourly_rate?.toString() || '0',
      start_cost: route.start_cost?.toString() || '0',
      stops: route.stops?.toString() || '',
      start_time: route.start_time || '08:00',
      end_time: route.end_time || '17:00',
      break_time: route.break_time || (route.customer === 'mover' ? '00:30' : '00:00'),
      notes: route.notes || ''
    });
    setEditingRoute(route);
    setAddDialog(true);
  }, []);

  // Inline editing
  const updateRouteInline = useCallback(async (route, field, newValue) => {
    if (route[field] === newValue) return;
    
    try {
      const updateData = { ...route, [field]: newValue };
      const response = await api.put(`/routes/item/${route.id}`, updateData);
      if (response.data.success) {
        toast.success(t('routes.messages.updated'));
        fetchRoutes();
      }
    } catch (error) {
      console.error('Error updating route:', error);
      toast.error(t('routes.messages.updateError'));
    }
  }, [fetchRoutes, t]);

  const updateRouteDriver = useCallback(async (route, employee) => {
    const driverName = employee.user?.full_name || `${employee.profile?.voornaam || ''} ${employee.profile?.naam || ''}`.trim();
    
    try {
      const updateData = { 
        ...route, 
        driver_id: employee.user?.id,
        driver_name: driverName 
      };
      
      const response = await api.put(`/routes/item/${route.id}`, updateData);
      if (response.data.success) {
        toast.success(t('routes.messages.driverUpdated'));
        fetchRoutes();
      }
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error(t('routes.messages.updateError'));
    }
  }, [fetchRoutes, t]);

  // Filter routes by search
  const filteredRoutes = routes.filter(route => {
    if (searchQuery) {
      return (
        turkishIncludes(route.driver_name || '', searchQuery) ||
        turkishIncludes(route.route_number || '', searchQuery) ||
        turkishIncludes(route.license_plate || '', searchQuery) ||
        turkishIncludes(route.plaats || '', searchQuery) ||
        turkishIncludes(route.copilot_name || '', searchQuery)
      );
    }
    return true;
  });

  return {
    // Data
    routes,
    filteredRoutes,
    firms,
    employees,
    vehicles,
    loading,
    
    // Filters
    selectedCustomers,
    selectedFirm,
    selectedMonths,
    searchQuery,
    customerList,
    dateRange,
    setSelectedFirm,
    setSelectedMonths,
    setSearchQuery,
    setDateRange,
    toggleCustomer,
    
    // Multi-customer helpers
    isMultiCustomer,
    primaryCustomer,
    
    // Bulk selection
    selectedRows,
    bulkDeleting,
    toggleRowSelection,
    toggleAllRows,
    handleBulkDelete,
    handleBulkClone,
    bulkCloning,
    recentlyClonedIds,
    
    // Form
    formData,
    setFormData,
    editingRoute,
    saving,
    addDialog,
    setAddDialog,
    handleSaveRoute,
    handleDeleteRoute,
    handleCloneRoute,
    resetForm,
    handleFormCustomerChange,
    openAddDialog,
    openEditDialog,
    
    // Inline editing
    updateRouteInline,
    updateRouteDriver,
    fetchEmployees,
    fetchRoutes,
  };
}
